import React, { useEffect, useState } from 'react';
import {
  getAIModelCredentialStatus,
  getSystemSettings,
  getAccountDetails,
  syncSystemAIPrompt,
  updateAIModelCredential,
  updateSystemSettings,
} from '../services/api';
import { AccountDetail, SystemSettings } from '../types';
import {
  Bot, Save, Lock, Sparkles, Mail, Settings as SettingsIcon,
  Eye, EyeOff, RefreshCw, Database, ToggleLeft, ToggleRight
} from 'lucide-react';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyMessage, setApiKeyMessage] = useState('');
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [syncPromptToAccounts, setSyncPromptToAccounts] = useState(false);
  const [selectedPromptAccounts, setSelectedPromptAccounts] = useState<string[]>([]);

  // Password visibility states
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [loadedSettings, credentialStatus] = await Promise.all([
        getSystemSettings(),
        getAIModelCredentialStatus(),
      ]);
      setSettings(loadedSettings);
      setApiKeyConfigured(credentialStatus.configured);
      setAccounts(await getAccountDetails());
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
      if(!settings) return;
      setSaving(true);
      try {
        await updateSystemSettings(settings);
        if (syncPromptToAccounts) {
          await syncSystemAIPrompt(settings.ai_custom_prompt || '', selectedPromptAccounts);
        }
        alert('系统配置已保存');
      } catch (e) {
        alert('保存失败：' + (e as Error).message);
      } finally {
        setSaving(false);
      }
  };

  const handleSaveApiKey = async () => {
    const value = apiKey.trim();
    if (!value) {
      setApiKeyMessage('请先填写模型 API Key');
      return;
    }
    setApiKeySaving(true);
    setApiKeyMessage('');
    try {
      await updateAIModelCredential(value);
      setApiKey('');
      setShowApiKey(false);
      setApiKeyConfigured(true);
      setApiKeyMessage('模型 API Key 已保存');
    } catch (error) {
      setApiKeyMessage(`保存失败：${error instanceof Error ? error.message : '请稍后重试'}`);
    } finally {
      setApiKeySaving(false);
    }
  };

  if (!settings) return <div className="p-8 text-center text-gray-400">加载配置中...</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
              <SettingsIcon className="w-6 h-6 text-gray-600" />
          </div>
          <div>
              <h2 className="text-3xl font-extrabold text-gray-900">系统设置</h2>
              <p className="text-gray-500 mt-1 text-sm font-medium">配置全局自动化规则与系统参数</p>
          </div>
        </div>
        <button
          onClick={loadSettings}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold text-gray-700 flex items-center gap-2 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          刷新
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-8">
          {/* Basic Settings */}
          <section className="space-y-4">
            <h3 className="text-lg font-extrabold text-gray-800 flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-gray-100 text-gray-600">
                    <Database className="w-4 h-4" />
                </div>
                基础设置
            </h3>

            <div className="ios-card rounded-[2rem] p-6 bg-white space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900">全局自动回复</div>
                  <div className="text-xs text-gray-500 mt-1">关闭后所有账号均不会自动发送关键词、AI 或默认回复</div>
                </div>
                <button
                  onClick={() => setSettings({...settings, auto_reply_enabled: !settings.auto_reply_enabled})}
                  className={`xsb-switch ${
                    settings.auto_reply_enabled ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                  data-state={settings.auto_reply_enabled ? 'on' : 'off'}
                  aria-label="切换全局自动回复"
                >
                  <div className="xsb-switch-thumb" />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900">允许用户注册</div>
                  <div className="text-xs text-gray-500 mt-1">开启后允许新用户注册账号</div>
                </div>
                <button
                  onClick={() => setSettings({...settings, registration_enabled: !settings.registration_enabled})}
                  className={`xsb-switch ${
                    settings.registration_enabled ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                  data-state={settings.registration_enabled ? 'on' : 'off'}
                >
                  <div className="xsb-switch-thumb" />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900">显示默认登录信息</div>
                  <div className="text-xs text-gray-500 mt-1">登录页面显示默认账号密码提示</div>
                </div>
                <button
                  onClick={() => setSettings({...settings, show_default_login_info: !settings.show_default_login_info})}
                  className={`xsb-switch ${
                    settings.show_default_login_info ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                  data-state={settings.show_default_login_info ? 'on' : 'off'}
                >
                  <div className="xsb-switch-thumb" />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900">登录滑动验证码</div>
                  <div className="text-xs text-gray-500 mt-1">开启后账号密码登录需要完成滑动验证</div>
                </div>
                <button
                  onClick={() => setSettings({...settings, login_captcha_enabled: !settings.login_captcha_enabled})}
                  className={`xsb-switch ${
                    settings.login_captcha_enabled ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                  data-state={settings.login_captcha_enabled ? 'on' : 'off'}
                >
                  <div className="xsb-switch-thumb" />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-bold text-gray-900">启用商品自动同步</div>
                  <div className="text-xs text-gray-500 mt-1">定时自动获取商品信息到本地数据库</div>
                </div>
                <button
                  onClick={() => setSettings({...settings, item_sync_enabled: !settings.item_sync_enabled})}
                  className={`xsb-switch ${
                    settings.item_sync_enabled ? 'bg-[#FFE815]' : 'bg-gray-300'
                  }`}
                  data-state={settings.item_sync_enabled ? 'on' : 'off'}
                >
                  <div className="xsb-switch-thumb" />
                </button>
              </div>

              <div className="space-y-3 px-4">
                <label className="block text-sm font-bold text-gray-800">商品同步间隔（分钟）</label>
                <input
                  type="number"
                  value={Math.round((settings.item_sync_interval || 600) / 60)}
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value) || 10;
                    setSettings({...settings, item_sync_interval: minutes * 60});
                  }}
                  className="w-full ios-input px-4 py-3 rounded-xl"
                  min="1"
                  max="1440"
                />
                <p className="text-xs text-gray-500">建议：10-60分钟</p>
              </div>

              <div className="space-y-3 px-4">
                <label className="block text-sm font-bold text-gray-800">每次最多同步页数</label>
                <input
                  type="number"
                  value={settings.item_sync_max_pages || 5}
                  onChange={(e) => setSettings({...settings, item_sync_max_pages: parseInt(e.target.value) || 5})}
                  className="w-full ios-input px-4 py-3 rounded-xl"
                  min="1"
                  max="50"
                />
                <p className="text-xs text-gray-500">每页20个商品</p>
              </div>
            </div>
          </section>

          {/* AI Configuration */}
          <section className="space-y-4">
            <h3 className="text-lg font-extrabold text-gray-800 flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-[#FFE815] text-black">
                    <Sparkles className="w-4 h-4" />
                </div>
                AI 智能回复配置
            </h3>

            <div className="ios-card rounded-[2rem] p-6 bg-white space-y-6">
              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-800">模型接口地址</label>
                <input
                  type="text"
                  value={settings.ai_api_url || ''}
                  onChange={e => setSettings({...settings, ai_api_url: e.target.value})}
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  placeholder="https://api.openai.com/v1"
                />
                <p className="text-xs text-gray-500">填写 OpenAI 兼容接口的 Base URL，无需补全 /chat/completions。</p>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-800">模型名称</label>
                <input
                  type="text"
                  value={settings.ai_model || ''}
                  onChange={e => setSettings({...settings, ai_model: e.target.value})}
                  className="w-full ios-input px-4 py-3 rounded-xl"
                  placeholder="例如：gpt-4.1-mini、qwen-plus 或您的模型名称"
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500">普通文本输入，按模型服务商提供的实际模型名填写。</p>
              </div>

              <div className="space-y-3 border-t border-gray-100 pt-6">
                <div className="flex items-center justify-between gap-4">
                  <label className="block text-sm font-bold text-gray-800">模型 API Key</label>
                  <span className={`text-xs font-medium ${apiKeyConfigured ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {apiKeyConfigured ? '已配置' : '未配置'}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(event) => setApiKey(event.target.value)}
                    className="w-full ios-input px-4 py-3 pr-12 rounded-xl text-sm"
                    placeholder={apiKeyConfigured ? '输入新 Key 可替换当前配置' : '输入模型服务商提供的 API Key'}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowApiKey(!showApiKey)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors" title={showApiKey ? '隐藏 API Key' : '显示 API Key'}>
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-500">保存后不会再回显完整 Key；修改模型接口或名称后请点击页面右下角保存。</p>
                  <button type="button" onClick={handleSaveApiKey} disabled={apiKeySaving} className="shrink-0 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-bold disabled:opacity-50">
                    {apiKeySaving ? '保存中...' : '保存 Key'}
                  </button>
                </div>
                {apiKeyMessage && <p className="text-xs font-medium text-gray-700">{apiKeyMessage}</p>}
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-800">AI 提示词配置</label>
                <textarea
                  className="w-full ios-input px-4 py-3 rounded-xl min-h-[100px] text-sm resize-none"
                  value={settings.ai_custom_prompt || ''}
                  onChange={e => setSettings({...settings, ai_custom_prompt: e.target.value})}
                  placeholder="输入店铺统一的回复规则，例如：回复简洁自然；优先说明商品真实情况；不承诺未确认的发货时间；售后、退款和联系方式问题转人工。"
                ></textarea>
                <p className="text-xs text-gray-500">系统会把这里的规则与商品信息、客户当前问题、最近聊天上下文和议价限制一起交给模型。</p>
                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <label className="flex items-center gap-3 text-sm font-bold text-gray-800">
                    <input
                      type="checkbox"
                      checked={syncPromptToAccounts}
                      onChange={e => setSyncPromptToAccounts(e.target.checked)}
                      className="h-4 w-4 accent-yellow-400"
                    />
                    将新提示词同步到指定账号
                  </label>
                  <p className="text-xs text-gray-500">未选中的账号保留自己的专用提示词；没有专用提示词的账号会自动继承全局提示词。</p>
                  {syncPromptToAccounts && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl bg-gray-50 p-3">
                      {accounts.length === 0 ? (
                        <span className="text-xs text-gray-500">暂无可同步账号</span>
                      ) : accounts.map(account => (
                        <label key={account.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={selectedPromptAccounts.includes(account.id)}
                            onChange={e => setSelectedPromptAccounts(current =>
                              e.target.checked
                                ? [...current, account.id]
                                : current.filter(id => id !== account.id)
                            )}
                            className="h-4 w-4 accent-yellow-400"
                          />
                          <span className="truncate">{account.nickname || account.id}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </section>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* SMTP Settings */}
          <section className="space-y-4">
            <h3 className="text-lg font-extrabold text-gray-800 flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-blue-100 text-blue-600">
                    <Mail className="w-4 h-4" />
                </div>
                SMTP 邮件配置
            </h3>

            <div className="ios-card rounded-[2rem] p-6 bg-white space-y-6">
              <p className="text-sm text-gray-500">配置SMTP服务器用于发送注册验证码等邮件通知</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-800">SMTP服务器</label>
                  <input
                    type="text"
                    value={settings.smtp_server || ''}
                    onChange={e => setSettings({...settings, smtp_server: e.target.value})}
                    placeholder="smtp.qq.com"
                    className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-bold text-gray-800">SMTP端口</label>
                  <input
                    type="number"
                    value={settings.smtp_port || 587}
                    onChange={e => setSettings({...settings, smtp_port: parseInt(e.target.value)})}
                    placeholder="587"
                    className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-800">发件邮箱</label>
                <input
                  type="email"
                  value={settings.smtp_user || ''}
                  onChange={e => setSettings({...settings, smtp_user: e.target.value})}
                  placeholder="your-email@qq.com"
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-800">邮箱密码/授权码</label>
                <div className="relative">
                  <input
                    type={showSmtpPassword ? 'text' : 'password'}
                    value={settings.smtp_password || ''}
                    onChange={e => setSettings({...settings, smtp_password: e.target.value})}
                    placeholder="输入密码或授权码"
                    className="w-full ios-input px-4 py-3 pr-12 rounded-xl text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">QQ邮箱需要使用授权码</p>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-800">发件人显示名（可选）</label>
                <input
                  type="text"
                  value={settings.smtp_from || ''}
                  onChange={e => setSettings({...settings, smtp_from: e.target.value})}
                  placeholder="闲鱼自动回复系统"
                  className="w-full ios-input px-4 py-3 rounded-xl text-sm"
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Save Button */}
      <div className="fixed bottom-10 right-10 z-30">
        <button
            onClick={handleSave}
            disabled={saving}
            className="ios-btn-primary px-10 py-5 rounded-[2rem] text-lg shadow-2xl shadow-yellow-200 flex items-center gap-3 transform hover:scale-105 active:scale-95 transition-all disabled:opacity-70"
        >
            <Save className="w-6 h-6" />
            {saving ? '保存中...' : '保存所有配置'}
        </button>
      </div>
    </div>
  );
};

export default Settings;
